import React, { ChangeEvent, forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { ICON_SIZE } from './consts';
import { Icon } from '../Icon';
import { classnames } from '@src/utils/classnames';
import { getClasses } from '@src/utils/getClass';
import { IInputProps } from './models';
import { Button } from '../Button';
import { InputIcon } from '../InputIcon';

import styled from './Input.scss';
import { Box } from '../Box';

/** Поле ввода */
export const Input = forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      className,
      preIcon,
      postIcon,
      number,
      size = 'md',
      label,
      value: propsValue,
      id,
      password,
      showResetButton = false,
      defaultValue = '',
      errorMessage,
      helpMessage,
      button,
      view = 'light',
      isHelpMessage,
      onBlur,
      onFocus,
      onChange,
      showNotification,
      ...rest
    },
    ref,
  ) => {
    const [value, setValue] = useState(defaultValue || propsValue || '');
    const [showPassword, setShowPassword] = useState(!password);
    const innerRef = useRef<HTMLInputElement>(null);
    const [isFocus, setIsFocus] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useImperativeHandle(ref, () => innerRef.current!, []);

    /** Вычисляемое значение нужно, чтобы отображать внешнее значение инпута или внутреннее */
    const mergedValue = useMemo(() => {
      if (propsValue !== undefined) return propsValue;
      return value || '';
    }, [value, propsValue]);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.value;

        if (propsValue === undefined) {
          setValue(newVal);
        }

        onChange?.(e);
      },
      [onChange, propsValue],
    );

    const handleReset = (e) => {
      setValue('');
      e.target.value = '';
      onChange?.(e);
      innerRef.current?.focus();
    };

    const handleFocus = useCallback(
      (e) => {
        setIsFocus(true);
        onFocus?.(e);
      },
      [onFocus],
    );

    const handleBlur = useCallback(
      (e) => {
        setIsFocus(false);
        onBlur?.(e);
      },
      [onBlur],
    );

    const iconSize = ICON_SIZE[size];
    const isSmallLabel = Boolean(mergedValue) || typeof mergedValue === 'number' || isFocus;
    const isButton = Boolean(button);
    const isPlaceholder = Boolean(!label || isSmallLabel);

    const rootClasses = classnames(styled.root, ...getClasses(styled, { size, view }), {
      [styled.iconPre]: Boolean(!password && preIcon),
      [styled.iconPost]: Boolean(postIcon || password || showResetButton || isButton),
      [styled.error]: Boolean(errorMessage),
    });
    const containerClasses = classnames(className, styled.container, {
      [styled.number]: Boolean(!password && number),
    });

    return (
      <div className={rootClasses}>
        <div className={containerClasses} data-size={size} data-elem="container" data-placeholder={isPlaceholder}>
          <div data-elem="label" data-size={size} className={styled.label} data-label-small={isSmallLabel}>
            {label}
          </div>

          {!password && preIcon && <InputIcon config={preIcon} data-elem="preIcon" data-size={size} size={iconSize} />}

          {showNotification && mergedValue && <Box className={styled.counter} data-id={'inputValueCounter'} />}

          <input
            {...rest}
            data-elem="input"
            data-size={size}
            type={showPassword ? 'text' : 'password'}
            ref={innerRef}
            value={mergedValue}
            data-id={id}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <div data-elem="postIcon" data-size={size}>
            {password ? (
              <Icon
                data-size={size}
                id={'showPasswordBtn'}
                name={showPassword ? 'eyeClosed' : 'eye'}
                size={iconSize}
                className={styled.iconEvent}
                onClick={() => setShowPassword(!showPassword)}
              />
            ) : (
              <>
                {postIcon && <InputIcon config={postIcon} size={iconSize} data-size={size} />}
                {showResetButton && mergedValue && (
                  <Icon
                    name="close"
                    size={iconSize}
                    data-size={size}
                    id={'showResetBtn'}
                    className={styled.iconEvent}
                    onClick={handleReset}
                  />
                )}
              </>
            )}

            {button && (
              <Button
                {...button}
                data-size={size}
                data-elem="button"
                disabled={rest.disabled}
                view={view === 'dark' ? 'primaryDark' : undefined}
              />
            )}
          </div>
        </div>

        {isHelpMessage && (
          <>
            {errorMessage ? (
              <div data-elem="message" data-id="errorMsg">
                {errorMessage}
              </div>
            ) : (
              <div data-elem="message" data-id="helpMsg">
                {helpMessage}
              </div>
            )}
          </>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
